import { useMemo } from 'react';
import { Duration } from 'luxon';
import { useIntl, MessageDescriptor } from 'react-intl';
import { DURATION_HH_MM_SS } from 'utils/date-time';
export const getObject = (
  duration: number
): { hours: number; minutes: number; seconds: number } => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor(duration / 60) - hours * 60;
  const seconds = duration % 60;
  return { hours, minutes, seconds };
};

export const getRoundedDuration = (duration: number): number => {
  const { seconds } = getObject(duration);

  return seconds > 30
    ? Duration.fromObject({ seconds: duration }).plus({ seconds: 60 }).seconds
    : duration;
};

export type UseFormattedDurationType = {
  longDurationFormat?: boolean;
  duration: number;
  needsLeadingZeroes?: boolean;
  needsMask?: boolean;
};

type getFormattedDurationProps = UseFormattedDurationType & {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, any>
  ) => string;
};

export const getFormattedDuration = ({
  formatMessage,
  duration,
  needsLeadingZeroes,
  needsMask,
  longDurationFormat
}: getFormattedDurationProps): string => {
  const { hours, minutes } = getObject(getRoundedDuration(duration));

  const formattedDurationWithoutMask = needsLeadingZeroes
    ? `${hours > 9 ? hours : `0${hours}`}:${
        minutes > 9 ? minutes : `0${minutes}`
      }`
    : `${hours}:${minutes}`;

  const formattedDurationWithMask =
    hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  const shortFormattedDuration = needsMask
    ? formattedDurationWithMask
    : formattedDurationWithoutMask;

  const longDuration = Duration.fromObject({ seconds: duration || 0 }).toFormat(
    DURATION_HH_MM_SS
  );

  const longFormattedDuration = needsMask
    ? formatMessage(
        { id: 'note.durationWithHours' },
        {
          duration: longDuration
        }
      )
    : longDuration;

  return longDurationFormat ? longFormattedDuration : shortFormattedDuration;
};

export const useFormattedDuration = (
  props: UseFormattedDurationType
): string => {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    return getFormattedDuration({ formatMessage, ...props });
  }, [props, formatMessage]);
};
